import React from "react";
import type * as controls from "../PhoenixFormsV4.Controls";
import { usePhoenixForms } from "../PhoenixFormsProvider";
import PhoenixOutput from "./PhoenixOutput";
import { PhoenixSelectGroupReadOnly, willPhoenixSelectGroupReadOnlyRender } from "./PhoenixSelectGroup";
import { PhoenixSelectGroupItemReadOnly } from "./PhoenixSelectGroupItem";
import { PhoenixRepeatReadOnly } from "./PhoenixRepeat";
import { PhoenixRepeatObjectReadOnly } from "./PhoenixRepeatObject";
import { PhoenixGroupReadOnly } from "./PhoenixGroup";
import { PhoenixSelectReadOnly, willPhoenixSelectReadOnlyRender } from "./PhoenixSelect";
import { PhoenixSelect1ReadOnly, willPhoenixSelect1ReadOnlyRender } from "./PhoenixSelect1";
import PhoenixPhoto from "./PhoenixPhoto";
import PhoenixPhotoTagGroup from "./PhoenixPhotoTagGroup";
import PhoenixRulesetDisplay from "./PhoenixRulesetDisplay";
import PhoenixRecommendationsetDisplay from "./PhoenixRecommendationsetDisplay";
import PhoenixFileControl from "./PhoenixFileControl";


interface Props {
  controlProps: controls.Control;
  parentControlProps?: controls.Control;
};

/**
 * Represents a controls when it is flagged by the forms engine as readonly during normal rendering (i.e. if it is a calculated control)
 *  or a control populated under a PhoenixFormsInstance with the readonly prop set to true.
 */
const PhoenixFormsV4ReadOnlyControl = (
  {
    controlProps,
    parentControlProps,
  }: Props
) => {

  const { getProxy, getWebRuntimeAppData } = usePhoenixForms();

  if (!controlProps.visible) return null;

  const controlFactory = () => {
    switch (controlProps.type) {
      case "input":
      case "textarea":
        // text controls... render as label control but only if value is not empty
        return controlProps.value ? <PhoenixOutput controlProps={controlProps as controls.OutputControl} /> : null;
      case "output":
        // readonly as-is
        return <PhoenixOutput controlProps={controlProps as controls.OutputControl} />;
      case "select":
        return willPhoenixSelectReadOnlyRender(controlProps as controls.ListFormControl) && <PhoenixSelectReadOnly controlProps={controlProps as controls.ListFormControl} />;
      case "select1":
        return willPhoenixSelect1ReadOnlyRender(controlProps as controls.ListFormControl) && <PhoenixSelect1ReadOnly controlProps={controlProps as controls.ListFormControl} />;
      case "selectgroup":
        return willPhoenixSelectGroupReadOnlyRender(controlProps as controls.ContainerControl) && <PhoenixSelectGroupReadOnly controlProps={controlProps as controls.ContainerControl} />;
      case "selectgroupitem":
        return <PhoenixSelectGroupItemReadOnly controlProps={controlProps as controls.SelectGroupItem} />;
      case "repeat":
        return <PhoenixRepeatReadOnly controlProps={controlProps as controls.RepeatControl} />;
      case "repeatobject":
        return <PhoenixRepeatObjectReadOnly controlProps={controlProps as controls.RepeatObject} parentControlProps={parentControlProps as controls.RepeatControl} />;
      case "group": 
        return <PhoenixGroupReadOnly controlProps={controlProps as controls.ContainerControl} />;
      case "photo":
        // complex control, readonly is a prop
        return <PhoenixPhoto controlProps={controlProps as controls.ContainerControl} proxy={getProxy()} webRuntimeData={getWebRuntimeAppData()} readonly />;
      case "phototaggroup":
        // readonly as-is
        return <PhoenixPhotoTagGroup controlProps={controlProps as controls.PhotoTagGroup} />;
      case "rulesetdisplay":
        // readonly as-is
        return <PhoenixRulesetDisplay controlProps={controlProps as controls.RulesetDisplay}  />;
      case "recommendationsetdisplay":
        // readonly as-is
        return <PhoenixRecommendationsetDisplay controlProps={controlProps as controls.RecommendationsetDisplay} />;
      case "file":
        return <PhoenixFileControl controlProps={controlProps as controls.FormControl} proxy={getProxy()} webRuntimeData={getWebRuntimeAppData()} readonly />;
      default:
        return <div>InvalidControl</div>;
    }
  };

  const className = `formcontrol readonly ${controlProps.type}`;

  let control = controlFactory();

  if (control) {
    return (
      <div className={className}>
        {control}
      </div>
    );
  } else {
    return null;
  }
};

export default PhoenixFormsV4ReadOnlyControl;